// Asking kadmon — T35 (3 Sep 2026); inside a chat since T55 (9 Sep 2026), agents doc § 7.2.
//
// ONE CALL, AND THE BROWSER CARRIES NO CONVERSATION. Until T55 this app held the thread between
// questions and sent it back on every follow-up. It does not any more: a question names the chat
// it belongs to, and the service rebuilds what the model sees from that chat's own rows. A
// recruiter can therefore continue a chat somebody else started, in a tab that has never seen a
// turn of it — which is the whole point of one shared history.
//
// A POST that ends in a refetch, like every write in this app: the chat's turns
// and the list's order are the service's answer, so the panel asks for them again rather than
// patching what it just sent. What the cards change — "Add to search" — is still the pipeline PUT
// in api/searches.ts, which invalidates the search keys as it always has.
//
// ONE ROUTE SINCE T81 (20 Sep 2026). Every ask this app makes — typed, dictated, or voice mode's
// — goes to `POST /find/stream`, so the answer can be drawn as it is written (app/VeraPanel.tsx,
// src/writing.ts). The plain `POST /find` is still the brain API's own route, with its own tests
// (`services/bff/src/find.db.test.ts`); nothing in the browser calls it, and the hook that did was
// removed here rather than left as a second way in that nobody exercises.
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { FindBody, FindResponse, FindStreamEvent } from '@vera/bff';
import { API_PREFIX, ApiError, type StreamFn } from './core.ts';
import { KEYS } from './mutation.ts';
import { useRequest, useStreamRequest } from './provider.tsx';

/** What voice mode hears, and the screen draws, while a streamed find runs (T66b, T81). */
export interface FindProgressHandlers {
  /** The model reached for a tool: a moment more. T570: with `line`, her `say_working` line. */
  working?(line?: string): void;
  /** One sentence of the answer, as the model writes it. */
  sentence?(text: string): void;
  /**
   * The whole answer once the brain has taken it (T202 K-23, 5 Oct 2026: after its matches are
   * written and every check; until then, from 16 Sep 2026, at its closing quote), with how many
   * confirm cards the ask proposed by then — what the voices speak. The cards' ids come with the
   * response.
   */
  answer?(text: string, cards: number): void;
  /**
   * T446d: the stream's first line — the chat and the row this ask opened, and whether a typed
   * follow-up may fold into it (`foldable`: no file, no link, not dictated).
   */
  opened?(opened: { chatId: string; queryId: string; foldable: boolean }): void;
}

/** T446d: the ask was stopped for a newer message; the panel asks the two as one. */
export interface FindSuperseded {
  superseded: true;
}

/**
 * The same ask, answered as it happens (T66b): `POST /find/stream`, a line per event, the last
 * the whole answer. A failure after the first byte arrives as the last line and is thrown as the
 * `ApiError` the plain ask would have thrown, so the panel explains it the same way — except after
 * the `answer` line (17 Sep 2026): voice mode has spoken that answer and the chat keeps it, so what
 * failed is what follows it, the matches, and the code says so (`find_incomplete`).
 *
 * T446d (25 Oct 2026): the stream opens with an `opened` line (the chat, the row, whether a typed
 * follow-up may fold into it) and may end with `superseded` instead of an answer: the asker sent
 * another message and the service stopped. An older service sends neither, and nothing changes.
 */
export async function askStreaming(
  stream: StreamFn,
  body: FindBody,
  on: FindProgressHandlers,
): Promise<FindResponse | FindSuperseded> {
  let response: FindResponse | null = null;
  let failure: ApiError | null = null;
  let answered = false;
  let superseded = false;
  await stream(`${API_PREFIX}/find/stream`, body, (line) => {
    const event = line as FindStreamEvent;
    if (event.type === 'opened')
      on.opened?.({ chatId: event.chatId, queryId: event.queryId, foldable: event.foldable });
    else if (event.type === 'superseded') superseded = true;
    else if (event.type === 'sentence') on.sentence?.(event.text);
    else if (event.type === 'answer') {
      answered = true;
      on.answer?.(event.text, event.cards);
    } else if (event.type === 'working') on.working?.(event.line);
    else if (event.type === 'done') response = event.response;
    else if (event.type === 'error')
      failure = new ApiError(
        502,
        answered ? 'find_incomplete' : event.error,
        'the streamed find failed',
      );
  });
  if (failure) throw failure;
  if (superseded) return { superseded: true };
  if (!response) throw new ApiError(0, 'unreadable', 'the streamed find ended without an answer');
  return response;
}

/** The streamed ask as a hook: the call, then the same refetch a plain ask ends in. */
export function useFindStreaming(): (
  body: FindBody,
  on: FindProgressHandlers,
) => Promise<FindResponse | FindSuperseded> {
  const stream = useStreamRequest();
  const queryClient = useQueryClient();
  return useCallback(
    async (body, on) => {
      const response = await askStreaming(stream, body, on);
      await Promise.all(KEYS.chat.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
      return response;
    },
    [stream, queryClient],
  );
}

/**
 * T446d: tell the service the question in flight has a newer message behind it
 * (`POST /find/:queryId/follow`). Best effort: a failure is ignored and her answer simply finishes.
 */
export function useFollowFind(): (queryId: string) => Promise<void> {
  const request = useRequest();
  return useCallback(
    async (queryId) => {
      try {
        await request<void>(`${API_PREFIX}/find/${encodeURIComponent(queryId)}/follow`, {
          method: 'POST',
        });
      } catch {
        // The stamp is a hint; without it the first answer is simply given in full.
      }
    },
    [request],
  );
}
